/**
 * mdlondon - Accessibility Enhancements
 * WCAG 2.1 AA compliance improvements
 * Focus management, keyboard navigation, ARIA labels, screen reader support
 */

class AccessibilityManager {
  constructor() {
    this.focusableElements = 'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';
    this.init();
  }

  init() {
    this.enhanceFocusManagement();
    this.enhanceKeyboardNavigation();
    this.enhanceARIALabels();
    this.enhanceModals();
    this.enhanceFormAccessibility();
    this.enhanceImageAccessibility();
    this.enhanceSkipLinks();
    this.enhanceScrollableRegions();
    this.enhanceIframes();
    this.fixListStructure();
    this.fixNestedContentinfo();
    this.detectUserPreferences();
    this.announcePageChanges();

    // Dispatch event to signal that accessibility enhancements are complete
    document.dispatchEvent(new CustomEvent('accessibilityEnhancementsComplete'));
  }

  /**
   * Enhance focus management for better keyboard navigation
   */
  enhanceFocusManagement() {
    // Add visible focus indicator
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Tab') {
        document.body.classList.add('user-is-tabbing');
      }
    });

    document.addEventListener('mousedown', () => {
      document.body.classList.remove('user-is-tabbing');
    });

    // Trap focus in modals
    this.setupFocusTrap();

    // Return focus to trigger element when closing modals
    this.setupFocusReturn();
  }

  /**
   * Trap focus within modals/dialogs
   */
  setupFocusTrap() {
    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Tab') return;

      const modal = document.querySelector('[role="dialog"]:not([aria-hidden="true"])');
      if (!modal) return;

      const focusableContent = modal.querySelectorAll(this.focusableElements);
      const firstFocusable = focusableContent[0];
      const lastFocusable = focusableContent[focusableContent.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === firstFocusable) {
          lastFocusable.focus();
          e.preventDefault();
        }
      } else {
        if (document.activeElement === lastFocusable) {
          firstFocusable.focus();
          e.preventDefault();
        }
      }
    });
  }

  /**
   * Return focus to trigger element when closing modals
   */
  setupFocusReturn() {
    let lastFocusedElement = null;

    // Store focused element before opening modal
    document.addEventListener('click', (e) => {
      if (e.target.hasAttribute('data-modal-trigger')) {
        lastFocusedElement = e.target;
      }
    });

    // Return focus when modal closes
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'aria-hidden') {
          const modal = mutation.target;
          if (modal.getAttribute('aria-hidden') === 'true' && lastFocusedElement) {
            lastFocusedElement.focus();
            lastFocusedElement = null;
          }
        }
      });
    });

    document.querySelectorAll('[role="dialog"]').forEach((modal) => {
      observer.observe(modal, { attributes: true });
    });
  }

  /**
   * Enhance keyboard navigation
   */
  enhanceKeyboardNavigation() {
    // Close modals with Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        const openModal = document.querySelector('[role="dialog"]:not([aria-hidden="true"])');
        if (openModal) {
          const closeButton = openModal.querySelector('[data-modal-close]');
          if (closeButton) {
            closeButton.click();
          }
        }
      }
    });

    // Arrow key navigation for menus
    const menus = document.querySelectorAll('[role="menu"], [role="menubar"]');
    menus.forEach((menu) => {
      this.setupMenuNavigation(menu);
    });

    // Enter/Space activation for custom interactive elements
    document.addEventListener('keydown', (e) => {
      if ((e.key === 'Enter' || e.key === ' ') && e.target.hasAttribute('role')) {
        const role = e.target.getAttribute('role');
        if (role === 'button' || role === 'tab' || role === 'menuitem') {
          e.preventDefault();
          e.target.click();
        }
      }
    });
  }

  /**
   * Setup arrow key navigation for menus
   */
  setupMenuNavigation(menu) {
    const menuItems = Array.from(menu.querySelectorAll('[role="menuitem"]'));

    menu.addEventListener('keydown', (e) => {
      const currentIndex = menuItems.indexOf(document.activeElement);
      let nextIndex;

      switch (e.key) {
        case 'ArrowDown':
        case 'ArrowRight':
          e.preventDefault();
          nextIndex = (currentIndex + 1) % menuItems.length;
          menuItems[nextIndex].focus();
          break;
        case 'ArrowUp':
        case 'ArrowLeft':
          e.preventDefault();
          nextIndex = (currentIndex - 1 + menuItems.length) % menuItems.length;
          menuItems[nextIndex].focus();
          break;
        case 'Home':
          e.preventDefault();
          menuItems[0].focus();
          break;
        case 'End':
          e.preventDefault();
          menuItems[menuItems.length - 1].focus();
          break;
      }
    });
  }

  /**
   * Enhance ARIA labels for better screen reader support
   */
  enhanceARIALabels() {
    // Add ARIA labels to interactive elements without text
    document.querySelectorAll('button:not([aria-label]), a:not([aria-label])').forEach((el) => {
      const icon = el.querySelector('svg, img');
      if (icon && !el.textContent.trim()) {
        // Try to infer label from context
        const label = this.inferLabel(el);
        if (label) {
          el.setAttribute('aria-label', label);
        }
      }
    });

    // Add ARIA labels to form inputs without labels
    document.querySelectorAll('input:not([aria-label]), select:not([aria-label]), textarea:not([aria-label])').forEach((input) => {
      if (!this.hasVisibleLabel(input)) {
        const placeholder = input.getAttribute('placeholder');
        const name = input.getAttribute('name');
        const label = placeholder || this.formatName(name);
        if (label) {
          input.setAttribute('aria-label', label);
        }
      }
    });

    // Add ARIA live regions for dynamic content
    this.setupLiveRegions();
  }

  /**
   * Infer label from element context
   */
  inferLabel(element) {
    // Check for title attribute
    const title = element.getAttribute('title');
    if (title) return title;

    // Check for data attributes
    const dataLabel = element.getAttribute('data-label');
    if (dataLabel) return dataLabel;

    // Check class names for common patterns
    const classList = element.className;
    if (classList.includes('close')) return 'Close';
    if (classList.includes('menu')) return 'Menu';
    if (classList.includes('search')) return 'Search';
    if (classList.includes('cart')) return 'Cart';
    if (classList.includes('wishlist')) return 'Wishlist';
    if (classList.includes('account')) return 'Account';

    return null;
  }

  /**
   * Check if input has visible label
   */
  hasVisibleLabel(input) {
    const id = input.getAttribute('id');
    if (id) {
      const label = document.querySelector(`label[for="${id}"]`);
      if (label) return true;
    }

    const parentLabel = input.closest('label');
    return !!parentLabel;
  }

  /**
   * Format name attribute to readable label
   */
  formatName(name) {
    if (!name) return '';
    return name
      .replace(/[_-]/g, ' ')
      .replace(/([A-Z])/g, ' $1')
      .trim()
      .replace(/^\w/, (c) => c.toUpperCase());
  }

  /**
   * Setup ARIA live regions for announcements
   */
  setupLiveRegions() {
    // Create polite live region
    if (!document.getElementById('a11y-live-polite')) {
      const polite = document.createElement('div');
      polite.id = 'a11y-live-polite';
      polite.setAttribute('aria-live', 'polite');
      polite.setAttribute('aria-atomic', 'true');
      polite.className = 'sr-only';
      document.body.appendChild(polite);
    }

    // Create assertive live region
    if (!document.getElementById('a11y-live-assertive')) {
      const assertive = document.createElement('div');
      assertive.id = 'a11y-live-assertive';
      assertive.setAttribute('aria-live', 'assertive');
      assertive.setAttribute('aria-atomic', 'true');
      assertive.className = 'sr-only';
      document.body.appendChild(assertive);
    }
  }

  /**
   * Announce message to screen readers
   */
  announce(message, priority = 'polite') {
    const liveRegion = document.getElementById(`a11y-live-${priority}`);
    if (liveRegion) {
      liveRegion.textContent = '';
      setTimeout(() => {
        liveRegion.textContent = message;
      }, 100);
    }
  }

  /**
   * Enhance modal accessibility
   */
  enhanceModals() {
    document.querySelectorAll('[role="dialog"]').forEach((modal) => {
      // Ensure modal has aria-modal
      if (!modal.hasAttribute('aria-modal')) {
        modal.setAttribute('aria-modal', 'true');
      }

      // Ensure modal has aria-labelledby or aria-label
      if (!modal.hasAttribute('aria-labelledby') && !modal.hasAttribute('aria-label')) {
        const heading = modal.querySelector('h1, h2, h3');
        if (heading) {
          const id = heading.id || `modal-title-${Date.now()}`;
          heading.id = id;
          modal.setAttribute('aria-labelledby', id);
        }
      }

      // Focus first focusable element when modal opens
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.attributeName === 'aria-hidden') {
            if (modal.getAttribute('aria-hidden') === 'false') {
              const firstFocusable = modal.querySelector(this.focusableElements);
              if (firstFocusable) {
                setTimeout(() => firstFocusable.focus(), 100);
              }
            }
          }
        });
      });

      observer.observe(modal, { attributes: true });
    });
  }

  /**
   * Enhance form accessibility
   */
  enhanceFormAccessibility() {
    // Add required attribute indicators
    document.querySelectorAll('input[required], select[required], textarea[required]').forEach((input) => {
      if (!input.hasAttribute('aria-required')) {
        input.setAttribute('aria-required', 'true');
      }
    });

    // Add aria-invalid for validation errors
    document.addEventListener('invalid', (e) => {
      e.target.setAttribute('aria-invalid', 'true');
    }, true);

    document.addEventListener('input', (e) => {
      if (e.target.hasAttribute('aria-invalid')) {
        if (e.target.validity.valid) {
          e.target.removeAttribute('aria-invalid');
        }
      }
    });

    // Announce form submission results
    document.addEventListener('submit', (e) => {
      const form = e.target;
      if (form.checkValidity()) {
        this.announce('Form submitted successfully');
      }
    });
  }

  /**
   * Enhance image accessibility
   */
  enhanceImageAccessibility() {
    // Add alt text to images without it
    document.querySelectorAll('img:not([alt])').forEach((img) => {
      // Check if image is decorative
      if (this.isDecorativeImage(img)) {
        img.setAttribute('alt', '');
        img.setAttribute('role', 'presentation');
      } else {
        // Try to infer alt text
        const alt = this.inferImageAlt(img);
        img.setAttribute('alt', alt);
      }
    });
  }

  /**
   * Check if image is decorative
   */
  isDecorativeImage(img) {
    // Check for decorative indicators
    if (img.classList.contains('decorative')) return true;
    if (img.getAttribute('role') === 'presentation') return true;

    // Check if image is in a link with text
    const link = img.closest('a');
    if (link && link.textContent.trim()) return true;

    return false;
  }

  /**
   * Infer alt text for image
   */
  inferImageAlt(img) {
    // Try title attribute
    const title = img.getAttribute('title');
    if (title) return title;

    // Try src filename
    const src = img.getAttribute('src');
    if (src) {
      const filename = src.split('/').pop().split('.')[0];
      return this.formatName(filename);
    }

    return 'Image';
  }

  /**
   * Enhance skip links
   */
  enhanceSkipLinks() {
    const skipLinks = document.querySelectorAll('.skip-to-content-link');
    skipLinks.forEach((link) => {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        const targetId = link.getAttribute('href').substring(1);
        const target = document.getElementById(targetId);
        if (target) {
          target.setAttribute('tabindex', '-1');
          target.focus();
          target.addEventListener('blur', () => {
            target.removeAttribute('tabindex');
          }, { once: true });
        }
      });
    });
  }

  /**
   * Enhance iframes with accessible names
   * WCAG 2.1 Success Criterion 4.1.2 (Level A) - frame-title
   */
  enhanceIframes() {
    // Find all iframes without title or aria-label
    const iframes = document.querySelectorAll('iframe:not([title]):not([aria-label])');

    iframes.forEach((iframe, index) => {
      // Check if it's the Shopify preview bar or admin
      const src = iframe.getAttribute('src') || '';

      if (src.includes('shopify.com') && (src.includes('admin') || src.includes('preview-bar'))) {
        iframe.setAttribute('title', 'Shopify Preview Bar');
        iframe.setAttribute('aria-label', 'Shopify Preview Bar');
      } else {
        // Generic title for other iframes
        const title = `Embedded content ${index + 1}`;
        iframe.setAttribute('title', title);
        iframe.setAttribute('aria-label', title);
      }
    });

    // Also watch for dynamically added iframes
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeName === 'IFRAME' && !node.hasAttribute('title') && !node.hasAttribute('aria-label')) {
            const src = node.getAttribute('src') || '';
            if (src.includes('shopify.com') && (src.includes('admin') || src.includes('preview-bar'))) {
              node.setAttribute('title', 'Shopify Preview Bar');
              node.setAttribute('aria-label', 'Shopify Preview Bar');
            } else {
              const existingIframes = document.querySelectorAll('iframe').length;
              const title = `Embedded content ${existingIframes}`;
              node.setAttribute('title', title);
              node.setAttribute('aria-label', title);
            }
          }
        });
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  /**
   * Fix list structure violations
   * WCAG 2.1 Success Criterion 1.3.1 (Level A) - list structure
   * Ensures lists only contain <li> elements as direct children
   */
  fixListStructure() {
    // Find all ul and ol elements
    const lists = document.querySelectorAll('ul, ol');

    lists.forEach((list) => {
      // Check each direct child
      Array.from(list.children).forEach((child) => {
        // If direct child is a list (ul or ol), wrap it in an li
        if (child.tagName === 'UL' || child.tagName === 'OL') {
          const li = document.createElement('li');
          list.insertBefore(li, child);
          li.appendChild(child);
        }
        // If direct child is not an li or a valid list element, wrap it in an li
        else if (child.tagName !== 'LI') {
          const li = document.createElement('li');
          list.insertBefore(li, child);
          li.appendChild(child);
        }
      });
    });
  }

  /**
   * Fix nested contentinfo landmarks
   * WCAG 2.1 Success Criterion 1.3.1 (Level A) & 2.4.1 (Level A)
   * Ensures there are no nested contentinfo landmarks and only one contentinfo per page
   */
  fixNestedContentinfo() {
    // Find all contentinfo landmarks (footer elements or elements with role="contentinfo")
    const contentinfoElements = Array.from(document.querySelectorAll('footer, [role="contentinfo"]'));

    if (contentinfoElements.length <= 1) {
      return; // No issue if there's 0 or 1 contentinfo landmark
    }

    // Check for nested contentinfo landmarks
    contentinfoElements.forEach((outer) => {
      const nestedContentinfo = outer.querySelectorAll('footer, [role="contentinfo"]');

      nestedContentinfo.forEach((inner) => {
        // Remove role="contentinfo" if it exists
        if (inner.hasAttribute('role') && inner.getAttribute('role') === 'contentinfo') {
          inner.removeAttribute('role');
        }

        // If it's a footer element, change it to a div to remove the implicit contentinfo role
        if (inner.tagName.toLowerCase() === 'footer') {
          const div = document.createElement('div');
          // Copy all attributes except role
          Array.from(inner.attributes).forEach((attr) => {
            if (attr.name !== 'role' || attr.value !== 'contentinfo') {
              div.setAttribute(attr.name, attr.value);
            }
          });
          // Move all children
          while (inner.firstChild) {
            div.appendChild(inner.firstChild);
          }
          // Replace footer with div
          inner.parentNode.replaceChild(div, inner);
        }
      });
    });
  }

  /**
   * Enhance scrollable regions for keyboard accessibility
   * WCAG 2.1 Success Criterion 2.1.1 (Level A)
   */
  enhanceScrollableRegions() {
    // Selectors for known scrollable containers
    const scrollableSelectors = [
      '.menu-list__scroll-container',
      '.menu-drawer__featured-content-list',
      '.menu-drawer__submenu',
      '.menu-drawer',
      '.predictive-search-form__content',
      '.product-media__drag-zoom-wrapper',
      '.search-modal__content',
      '.quick-add-modal__content-info',
      '.localization-form__select',
      '[data-scrollable]'
    ];

    // Find all potentially scrollable elements
    const elements = document.querySelectorAll(scrollableSelectors.join(', '));

    elements.forEach(element => {
      const style = window.getComputedStyle(element);
      const isScrollable =
        (style.overflowY === 'auto' || style.overflowY === 'scroll' ||
         style.overflowX === 'auto' || style.overflowX === 'scroll' ||
         style.overflow === 'auto' || style.overflow === 'scroll') &&
        !element.hasAttribute('tabindex') &&
        !element.closest('[tabindex]');

      if (isScrollable) {
        // Check if element actually has scrollable content
        const hasVerticalScroll = element.scrollHeight > element.clientHeight;
        const hasHorizontalScroll = element.scrollWidth > element.clientWidth;

        if (hasVerticalScroll || hasHorizontalScroll) {
          element.setAttribute('tabindex', '0');
          element.setAttribute('role', 'region');

          // Add aria-label if not present
          if (!element.getAttribute('aria-label') && !element.getAttribute('aria-labelledby')) {
            const label = element.className.split(' ')[0] || 'scrollable content';
            element.setAttribute('aria-label', label.replace(/-/g, ' ').replace(/__/g, ' '));
          }
        }
      }
    });

    // Re-run when DOM changes (for dynamically loaded content)
    const observer = new MutationObserver((mutations) => {
      let shouldRerun = false;
      mutations.forEach((mutation) => {
        if (mutation.addedNodes.length > 0) {
          shouldRerun = true;
        }
      });

      if (shouldRerun) {
        setTimeout(() => this.enhanceScrollableRegions(), 100);
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  /**
   * Detect and respect user preferences
   */
  detectUserPreferences() {
    // Detect prefers-reduced-motion
    const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

    const handleReducedMotionChange = (e) => {
      if (e.matches) {
        document.documentElement.classList.add('reduce-motion');
        this.announce('Reduced motion mode detected');
      } else {
        document.documentElement.classList.remove('reduce-motion');
      }
    };

    // Set initial state
    handleReducedMotionChange(reducedMotionQuery);

    // Listen for changes
    reducedMotionQuery.addEventListener('change', handleReducedMotionChange);

    // Detect prefers-contrast
    const contrastQuery = window.matchMedia('(prefers-contrast: high)');

    const handleContrastChange = (e) => {
      if (e.matches) {
        document.documentElement.classList.add('high-contrast');
      } else {
        document.documentElement.classList.remove('high-contrast');
      }
    };

    handleContrastChange(contrastQuery);
    contrastQuery.addEventListener('change', handleContrastChange);

    // Detect dark mode preference
    const darkModeQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const handleDarkModeChange = (e) => {
      if (e.matches) {
        document.documentElement.classList.add('prefers-dark');
      } else {
        document.documentElement.classList.remove('prefers-dark');
      }
    };

    handleDarkModeChange(darkModeQuery);
    darkModeQuery.addEventListener('change', handleDarkModeChange);
  }

  /**
   * Announce page changes for SPA-like navigation
   */
  announcePageChanges() {
    // Listen for page transition events
    let lastUrl = location.href;

    const observer = new MutationObserver(() => {
      if (location.href !== lastUrl) {
        lastUrl = location.href;
        const title = document.title;
        this.announce(`Navigated to ${title}`);
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }
}

// Initialize accessibility manager
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.mdAccessibilityManager = new AccessibilityManager();
  });
} else {
  window.mdAccessibilityManager = new AccessibilityManager();
}

// Export for use in other scripts
if (typeof module !== 'undefined' && module.exports) {
  module.exports = AccessibilityManager;
}
